import {
  Component,
  computed,
  inject,
  Inject,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { AbbSidePanelComponent } from '@components/abb-side-panel/abb-side-panel.component';
import {
  SIDE_PANEL_DATA,
  SidePanelData,
} from '@components/abb-side-panel/side-panel';
import { AutomationFormComponent } from '../automation-form/automation-form.component';
import { AbbCommonModule } from 'app/abb-common.module';
import { TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/ui/toast.service';
import { Apollo } from 'apollo-angular';
import {
  createAutomationMutation,
  updateAutomationMutation,
} from '@graphql/automations';
import {
  AutomationsDefinitionsQuery,
  CreateAutomationMutation,
  CreateAutomationMutationVariables,
  UpdateAutomationMutation,
  UpdateAutomationMutationVariables,
} from 'gql/graphql';
import { t } from '@utils/strings';

type AutomationsSidePanelData =
  | { mode: 'new' }
  | {
      mode: 'edit' | 'clone';
      defaultData: AutomationsDefinitionsQuery['automationsDefinitions'][number];
    };

@Component({
  selector: 'abb-automations-side-panel',
  standalone: true,
  template: `
    <abb-side-panel #panelRef contentWrapperClass="!px-0">
      <abb-automation-form
        [isLoading]="saving()"
        [mode]="data.mode"
        [initValues]="initValues()"
        (formSubmit)="onSubmit($event)"
        (onCancel)="onCancel()"
      ></abb-automation-form>
    </abb-side-panel>
  `,
  styles: ``,
  host: {
    class: 'w-[40rem]',
  },
  imports: [AbbCommonModule, AbbSidePanelComponent, AutomationFormComponent],
  providers: [
    { provide: TRANSLATION_PREFIX, useValue: 'controls.tabs.automations' },
  ],
})
export class AutomationsSidePanelComponent
  implements SidePanelData<AutomationsSidePanelData>
{
  @ViewChild('panelRef') panelRef: AbbSidePanelComponent | undefined;
  saving = signal(false);
  onSubmitted = output<boolean>();
  initValues = computed(() => {
    return this.data.mode === 'new' ? undefined : this.data.defaultData;
  });
  private prefix = inject(TRANSLATION_PREFIX);
  constructor(
    @Inject(SIDE_PANEL_DATA) public data: AutomationsSidePanelData,
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly toastService: ToastService
  ) {}

  onCancel() {
    this.panelRef?.dispose();
  }

  onSubmit(
    automationData:
      | CreateAutomationMutationVariables
      | UpdateAutomationMutationVariables
  ): void {
    this.saving.set(true);
    if (this.data.mode !== 'edit') {
      this.apollo
        .mutate<CreateAutomationMutation, CreateAutomationMutationVariables>({
          mutation: createAutomationMutation,
          variables: automationData,
        })
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.toastService.notifySuccess(
              t(
                this.prefix,
                `{name} was created successfully`,
                this.translate,
                { name: automationData.when.name }
              )
            );
            this.onSubmitted.emit(true);
            this.panelRef?.dispose();
            this.apollo.client.refetchQueries({
              include: ['AutomationsDefinitions'],
            });
          },
          error: () => {
            this.saving.set(false);
            this.toastService.notifyError(
              t(
                this.prefix,
                `An error occurred while creating {name}`,
                this.translate,
                { name: automationData.when.name }
              )
            );
            this.onSubmitted.emit(false);
          },
        });
    } else {
      this.apollo
        .mutate<UpdateAutomationMutation, UpdateAutomationMutationVariables>({
          mutation: updateAutomationMutation,
          variables: {
            id: this.data.defaultData.id,
            ...automationData,
          },
        })
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.toastService.notifySuccess(
              t(
                this.prefix,
                `{name} was updated successfully`,
                this.translate,
                { name: automationData.when.name }
              )
            );
            this.apollo.client.refetchQueries({
              include: ['AutomationsDefinitions'],
            });
            this.onSubmitted.emit(true);
            this.panelRef?.dispose();
          },
          error: () => {
            this.saving.set(false);
            this.toastService.notifyError(
              t(
                this.prefix,
                `An error occurred while updating {name}`,
                this.translate,
                { name: automationData.when.name }
              )
            );
            this.onSubmitted.emit(false);
          },
        });
    }
  }
}
